import { useState } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Download, FileJson, FileSpreadsheet, Database } from 'lucide-react';

export default function Backup() {
  const [loadingJson, setLoadingJson] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);

  const handleDownload = async (format) => {
    const setLoading = format === 'json' ? setLoadingJson : setLoadingCsv;
    setLoading(true);
    try {
      const res = await adminAPI.downloadBackup(format);
      const blob = new Blob([res.data], {
        type: format === 'json' ? 'application/json' : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `backup_mietwagen_gb_sylt_${date}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`${format.toUpperCase()}-Backup heruntergeladen`);
    } catch {
      toast.error('Backup konnte nicht erstellt werden');
    } finally {
      setLoading(false);
    }
  };

  const backupContents = [
    'CMS-Inhalte (55 Felder)',
    'SEO-Einstellungen',
    'Rechtstexte (Impressum, Datenschutz, Hinweise)',
    'Standorte und Adressen',
    'Einstellungen (Affiliate-URL, QR-Code-URL)',
    'Medien-Metadaten',
    'Benutzerdaten (ohne Passwoerter)',
  ];

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Backup</h1>
        <p className="text-sm text-slate-500 font-body mt-1">Manueller Export aller Daten</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm rounded-sm" data-testid="backup-json-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0E518D]/10 rounded-sm flex items-center justify-center">
                <FileJson className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">JSON-Backup</h3>
                <p className="text-xs text-slate-500 font-body">Vollstaendiger Export aller Daten</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 font-body mb-5 leading-relaxed">
              Exportiert alle Inhalte, Einstellungen und Metadaten als strukturierte JSON-Datei. Ideal fuer Archivierung und Wiederherstellung.
            </p>
            <Button
              onClick={() => handleDownload('json')}
              disabled={loadingJson}
              className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body"
              data-testid="backup-download-json"
            >
              {loadingJson ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              JSON-Backup herunterladen
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-sm" data-testid="backup-csv-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0E518D]/10 rounded-sm flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">CSV-Backup</h3>
                <p className="text-xs text-slate-500 font-body">Tabellarischer Export</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 font-body mb-5 leading-relaxed">
              Exportiert alle Daten als CSV-Datei. Kann direkt in Excel oder Google Sheets geoeffnet werden.
            </p>
            <Button
              onClick={() => handleDownload('csv')}
              disabled={loadingCsv}
              className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body"
              data-testid="backup-download-csv"
            >
              {loadingCsv ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              CSV-Backup herunterladen
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm rounded-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />
            <h3 className="font-heading text-lg font-semibold text-slate-900">Enthaltene Daten</h3>
          </div>
          <ul className="space-y-2">
            {backupContents.map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-600 font-body" data-testid={`backup-content-item-${i}`}>
                <div className="w-1.5 h-1.5 bg-[#0E518D] rounded-full flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
